import type { FormEvent } from "react";
import { useEffect, useId, useState } from "react";
import { Modal } from "@circleco/compass/components/Modal";
import { TextInput } from "@circleco/compass/components/TextInput";
import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";

export interface CreateCommunityModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (name: string) => void;
  /** Pre-fills the name field when the modal opens (e.g. "{first_name}'s Community"). */
  readonly defaultName?: string;
}

export const CreateCommunityModal = ({
  open,
  onOpenChange,
  onSubmit,
  defaultName = "",
}: CreateCommunityModalProps) => {
  const { t } = useCommunitySwitcherContext();
  const [name, setName] = useState(defaultName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formId = useId();

  useEffect(() => {
    if (open) setName(defaultName);
  }, [open, defaultName]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setIsSubmitting(true);
    onSubmit(trimmedName);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isSubmitting) return;
    onOpenChange(nextOpen);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          showCloseButton
          title={t("create_community_modal.title")}
        />
        <Modal.Body>
          <form id={formId} onSubmit={handleSubmit}>
            <TextInput
              autoFocus
              label={t("create_community_modal.name_label")}
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("create_community_modal.cancel"),
          }}
          primaryAction={{
            label: t("create_community_modal.continue"),
            type: "submit",
            form: formId,
            disabled: !name.trim() || isSubmitting,
            loading: isSubmitting,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
